import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { isDark } from '../common/utils';
import styles from './scroll-spy.module.scss';
const ScrollSpy = ({ links = [], children = [], linksPosition = 'left', }) => {
    const scrollSpyContentRef = React.useRef(null);
    const spy = useRef();
    // tracks the scroll direction
    const scrollDirection = useRef();
    // tracks the last selected link index
    const lastSelectedIndex = useRef(0);
    const isDarkMode = useMemo(() => isDark(), []);
    const [scrollSpyLinks, setScrollSpyLinks] = React.useState(links.map(link => ({
        active: false,
        id: `spy-${nanoid()}`,
        name: link,
    })));
    const [contents, setContents] = React.useState(Array.isArray(children)
        ? children.map((_, index) => ({
            active: false,
            hash: 0,
            id: scrollSpyLinks[index].id,
        }))
        : [
            {
                active: false,
                hash: 0,
                id: scrollSpyLinks[0].id,
            },
        ]);
    const onRef = useCallback((node) => {
        if (node) {
            scrollSpyContentRef.current = node;
            spy.current = new IntersectionObserver(entries => {
                const ids = entries.map(entry => [
                    entry.target.id,
                    entry.isIntersecting,
                ]);
                setContents(prev => prev.map(content => {
                    const found = ids.find(id => id[0] === content.id);
                    return found
                        ? {
                            ...content,
                            active: found[1],
                            hash: found && found[1] ? Date.now() : content.hash,
                        }
                        : content;
                }));
            }, {
                root: node,
            });
            const items = scrollSpyContentRef.current.querySelectorAll('.' + styles.header);
            if (items) {
                items.forEach(item => {
                    spy.current?.observe(item);
                });
            }
        }
    }, []);
    useEffect(() => {
        const found = contents
            .filter(c => c.active)
            .sort((a, b) => scrollDirection.current === 'up' ? b.hash - a.hash : a.hash - b.hash)[0];
        lastSelectedIndex.current = contents.findIndex(x => x === found);
        if (found) {
            setScrollSpyLinks(prev => prev.map(link => ({
                ...link,
                active: link.id === found.id,
            })));
        }
    }, [JSON.stringify(contents)]);
    const handleScrollTo = useCallback((id, index) => {
        if (lastSelectedIndex.current < index) {
            scrollDirection.current = 'down';
        }
        else {
            scrollDirection.current = 'up';
        }
        lastSelectedIndex.current = index;
        const element = scrollSpyContentRef.current?.querySelector(`#${id}`);
        const ref = scrollSpyContentRef.current;
        if (element && ref) {
            ref.scrollTo({
                behavior: 'smooth',
                top: element.offsetTop - ref.offsetTop,
            });
        }
    }, []);
    const handleScroll = useCallback((ev) => {
        if (ev.deltaY > 0) {
            scrollDirection.current = 'down';
        }
        else if (ev.deltaY < 0) {
            scrollDirection.current = 'up';
        }
    }, []);
    const spyWrapperClass = useMemo(() => {
        return classNames(styles.wrapper, {
            [styles[`wrapper_${linksPosition}`]]: true,
        });
    }, []);
    return (_jsxs("div", { className: spyWrapperClass, children: [_jsx("div", { className: styles.aside, children: _jsx("ul", { className: styles.list, children: scrollSpyLinks.map((link, index) => {
                        return (_jsx("li", { className: classNames(styles.list_item, link.active ? styles.active : '', isDarkMode ? styles.dark : ''), onClick: ev => {
                                ev.preventDefault();
                                handleScrollTo(link.id, index);
                            }, children: link.name }, link.id));
                    }) }) }), _jsx("div", { className: styles.content, ref: onRef, onWheel: handleScroll, children: contents.map((content, index) => {
                    return (_jsxs("div", { className: classNames(styles['content-item']
                        // content.active ? styles.active : ''
                        ), children: [_jsx("h4", { className: classNames(styles.header, {
                                    [styles.dark]: isDarkMode,
                                }), id: content.id, children: links[index] }), Array.isArray(children) ? children[index] : children] }, content.id));
                }) })] }));
};
ScrollSpy.displayName = 'ScrollSpy';
export { ScrollSpy };
